import React, { useEffect } from 'react';
import { Flame, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOnboardingConfig } from '@/hooks/useOnboardingConfig';
import { LazyEmbedPlayer } from '@/components/onboarding/LazyEmbedPlayer';

interface Props {
  onAccept: () => void;
  isSubmitting?: boolean;
}

export function PostLoginOnboardingModal({ onAccept, isSubmitting = false }: Props) {
  const { data: config, isLoading } = useOnboardingConfig('post_login');

  const title = config?.title || 'Bem-vindo à Forja Hub';
  const description = config?.description || '';
  const ctaLabel = config?.cta_label || 'Concluir';

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="post-login-onboarding-title"
      className="fixed inset-0 z-[9999] bg-background overflow-y-auto"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div className="sticky top-0 z-10 flex items-center justify-end px-4 py-3 bg-background/80 backdrop-blur border-b border-border/50">
        <button
          onClick={onAccept}
          disabled={isSubmitting}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
          aria-label="Já vi, fechar"
          title="Marca o onboarding como visto e não mostra novamente"
        >
          Já vi, fechar
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 md:py-10 flex flex-col">
        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-5 self-center">
          <Flame className="w-8 h-8 text-primary" />
        </div>
        <h1 id="post-login-onboarding-title" className="text-xl md:text-3xl font-bold text-foreground mb-2 text-center">
          {title}
        </h1>
        {description && (
          <p className="text-muted-foreground text-sm md:text-base text-center mb-6">
            {description}
          </p>
        )}

        <div className="mb-8">
          {isLoading ? (
            <div className="aspect-video w-full rounded-xl bg-secondary/40 animate-pulse" />
          ) : config?.video_url ? (
            <LazyEmbedPlayer videoUrl={config.video_url} />
          ) : (
            <div className="aspect-video w-full rounded-xl bg-secondary/40 border border-border/50 flex items-center justify-center text-sm text-muted-foreground">
              Vídeo em breve
            </div>
          )}
        </div>

        <Button
          size="lg"
          className="w-full h-12 md:h-14 text-base font-semibold"
          onClick={onAccept}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Salvando...' : ctaLabel}
        </Button>
      </div>
    </div>
  );
}